import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import LegalFooter from "@/components/legal/LegalFooter";
import { HardHat, Mail, ChevronRight, ArrowLeft, CheckCircle2 } from "lucide-react";

/**
 * Self-service Starter signup — /signup.
 *
 * Founder Post-Deployment Repair Directive (2026-09-29, superseding the
 * earlier no-card draft): a card is required before ANY trial access.
 * Both "Start free trial" and "Subscribe now" collect a card at Stripe
 * Checkout (Checkout's own `payment_method_collection` default of
 * "always" is left untouched) -- the only difference is whether that
 * card is charged today (`start_trial=false`, immediate) or in 14 days
 * (`start_trial=true`, the default self-service path). POSTs
 * /api/signup either way, then the owner verifies their email on
 * /accept-invite and is sent straight into the correctly-configured
 * Stripe Checkout Session for the plan/cadence chosen here.
 */
export default function Signup() {
  const navigate = useNavigate();
  const [startTrial, setStartTrial] = useState(true);
  const [cadence, setCadence] = useState("monthly"); // monthly | annual
  const [companyName, setCompanyName] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sentTo, setSentTo] = useState(null);

  const lookupKey = cadence === "annual" ? "essentials_annual" : "essentials_monthly";
  const priceLabel = cadence === "annual" ? "$1,290/yr" : "$129/mo";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/signup", {
        company_name: companyName.trim(),
        owner_name: ownerName.trim(),
        owner_email: ownerEmail.trim(),
        lookup_key: lookupKey,
        start_trial: startTrial,
      });
      setSentTo(data.email);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Couldn't create your account. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (sentTo) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-sm text-center" data-testid="signup-check-email">
            <Link to="/" className="inline-flex mb-8"><Logo /></Link>
            <CheckCircle2 size={40} className="text-[var(--fop-primary)] mx-auto mb-4" />
            <h2 className="font-display text-2xl text-[var(--fop-text)] mb-2">Check your email</h2>
            <p className="text-sm text-[var(--fop-text-2)] mb-1">
              We sent an activation link to <span className="text-[var(--fop-text)]">{sentTo}</span>.
            </p>
            <p className="text-sm text-[var(--fop-text-3)]">
              {startTrial
                ? `Set your password there, then add a card to start your 14-day free trial — ${priceLabel} billed automatically after, unless you cancel.`
                : "Set your password there to activate your account and complete checkout."}
            </p>
            <Link to="/login" className="btn btn-ghost mt-8 inline-flex" data-testid="signup-back-to-login">
              <ArrowLeft size={14} /> Back to sign in
            </Link>
          </div>
        </div>
        <LegalFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center p-6 sm:p-8">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between mb-6">
            <Link to="/" data-testid="signup-back-home"><Logo /></Link>
            <Link to="/login" className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--fop-text-3)] hover:text-[var(--fop-text)] inline-flex items-center gap-1.5">
              <ArrowLeft size={12} /> Sign in instead
            </Link>
          </div>

          <div className="fop-card p-1 flex mb-5" data-testid="signup-mode-toggle">
            <button type="button" onClick={() => setStartTrial(true)}
                    data-testid="signup-mode-trial"
                    className={`flex-1 rounded-md py-2.5 text-sm font-mono uppercase tracking-wider transition-colors ${startTrial ? "bg-[var(--fop-primary)] text-white" : "text-[var(--fop-text-2)]"}`}>
              Start Free Trial
            </button>
            <button type="button" onClick={() => setStartTrial(false)}
                    data-testid="signup-mode-subscribe"
                    className={`flex-1 rounded-md py-2.5 text-sm font-mono uppercase tracking-wider transition-colors ${!startTrial ? "bg-[var(--fop-primary)] text-white" : "text-[var(--fop-text-2)]"}`}>
              Subscribe Now
            </button>
          </div>

          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[var(--fop-primary)] mb-2">
            {startTrial ? "14-Day Free Trial · Card Required" : "ForgedOps Plans Starter"}
          </div>
          <h2 className="font-display text-3xl sm:text-4xl text-[var(--fop-text)] mb-1.5">
            {startTrial ? "Try ForgedOps Plans free for 14 days" : "Get your crew set up"}
          </h2>
          <p className="text-base text-[var(--fop-text-2)] mb-6">
            5 users · 3 active projects · 150 active plan pages · 15 GiB storage (~16.1 GB) · 100 Ask queries/mo.
            {startTrial
              ? ` $0 due today. Your card starts billing ${priceLabel} automatically after 14 days unless you cancel.`
              : " Real production access — pay by card, activate immediately."}
          </p>

          <div className="fop-card p-1 flex mb-6" data-testid="signup-cadence-toggle">
            <button type="button" onClick={() => setCadence("monthly")}
                    data-testid="signup-cadence-monthly"
                    className={`flex-1 rounded-md py-2.5 text-sm font-mono uppercase tracking-wider transition-colors ${cadence === "monthly" ? "bg-[var(--fop-primary)] text-white" : "text-[var(--fop-text-2)]"}`}>
              $129/mo
            </button>
            <button type="button" onClick={() => setCadence("annual")}
                    data-testid="signup-cadence-annual"
                    className={`flex-1 rounded-md py-2.5 text-sm font-mono uppercase tracking-wider transition-colors ${cadence === "annual" ? "bg-[var(--fop-primary)] text-white" : "text-[var(--fop-text-2)]"}`}>
              $1,290/yr
            </button>
          </div>

          <form onSubmit={submit} className="space-y-5" data-testid="signup-form">
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[var(--fop-text-2)] mb-2 block">
                Company name
              </label>
              <input
                type="text" required value={companyName} onChange={(e) => setCompanyName(e.target.value)}
                data-testid="signup-company-input" placeholder="Acme Excavation Co."
              />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[var(--fop-text-2)] mb-2 block">
                Your name
              </label>
              <input
                type="text" required value={ownerName} onChange={(e) => setOwnerName(e.target.value)}
                data-testid="signup-name-input" placeholder="Jamie Rivera"
              />
            </div>
            <div>
              <label className="text-xs font-mono uppercase tracking-wider text-[var(--fop-text-2)] mb-2 block">
                Work email
              </label>
              <input
                type="email" required value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)}
                data-testid="signup-email-input" placeholder="you@company.com"
              />
            </div>
            {error && (
              <div className="text-[var(--fop-danger)] text-sm font-mono px-3 py-2.5 rounded border border-[rgba(220,38,38,0.35)] bg-[rgba(220,38,38,0.08)]" data-testid="signup-error">
                {error}
              </div>
            )}
            <button type="submit" disabled={submitting}
                     className="btn btn-primary w-full mt-2"
                     style={{ minHeight: "48px", fontSize: "16px" }}
                     data-testid="signup-submit">
              <Mail size={16} />
              {submitting ? "Creating account…" : (startTrial ? "Continue to free trial" : "Create account")}
              {!submitting && <ChevronRight size={16} className="ml-auto" />}
            </button>
          </form>

          <p className="mt-5 text-xs text-[var(--fop-text-3)] text-center">
            {startTrial
              ? `You'll verify your email, then add a card in Stripe Checkout. $0 due today — ${priceLabel} bills automatically after 14 days unless you cancel.`
              : "You'll verify your email, then pay by card in Stripe Checkout to activate."}
          </p>
        </div>
      </div>
      <LegalFooter />
    </div>
  );
}
